import { useCallback, useEffect, useRef, useState } from "react";
import { analyze_recording } from "../../api";
import type { Recording } from "../../api/types";
import { useAnalysisSubscriptions } from "../../utils/useAnalysisSubscriptions";

/**
 * A recordings list loaded by `fetch` (reloaded whenever `fetch` changes), kept current as the
 * desktop app syncs new recordings — analysing `ownerId`'s fresh ones when `autoAnalyze` is on —
 * and as running analyses finish. `loadedKey` is the `key` of the last successful load, so a
 * caller can tell a list for older inputs from one for the current ones.
 *
 * `loading` means a list for the current inputs is still on its way; a reload of the list on
 * screen happens in the background, the list staying until its replacement arrives.
 * `error` means there is no list for the current inputs. `updateError` means the list is there
 * but may be stale: a reload or a live analysis update failed after it loaded. Either clears on
 * the next successful load, which `refresh` retries.
 */
export function useRecordingList({
  fetch,
  key,
  ownerId,
  autoAnalyze,
  recordingSyncCompletionId,
}: {
  fetch: () => Promise<Recording[]>;
  key: string;
  ownerId: string;
  autoAnalyze: boolean;
  recordingSyncCompletionId: number;
}) {
  const { subscribe } = useAnalysisSubscriptions();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  const loaded = useRef(false);
  // The key of the list on screen, read where the state would be a stale closure.
  const loadedKeyRef = useRef<string | null>(null);
  const recordsRef = useRef(recordings);
  recordsRef.current = recordings;
  const handledSync = useRef(recordingSyncCompletionId);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    try {
      const items = await fetch();
      if (request !== generation.current) return null;
      setRecordings(items);
      setLoadedKey(key);
      loadedKeyRef.current = key;
      setRevision(value => value + 1);
      setError(false);
      setUpdateError(false);
      loaded.current = true;
      return items;
    } catch {
      if (request === generation.current) {
        // a failed reload keeps the list it would have replaced
        if (loadedKeyRef.current === key) {
          setUpdateError(true);
        } else {
          setError(true);
          setUpdateError(false);
        }
      }
      return null;
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [fetch, key]);
  useEffect(() => {
    void load();
    return () => {
      generation.current++;
    };
  }, [load]);
  const refresh = useCallback(() => {
    void load();
  }, [load]);
  /** Applies a local edit, superseding any load in flight so it cannot overwrite the edit. */
  const update = useCallback((edit: (items: Recording[]) => Recording[]) => {
    generation.current++;
    setLoading(false);
    setRecordings(edit);
  }, []);
  useEffect(() => {
    if (handledSync.current === recordingSyncCompletionId) return;
    handledSync.current = recordingSyncCompletionId;
    const previous = new Set(recordsRef.current.map(item => item.id));
    const analyzeNew = autoAnalyze && loaded.current;
    void load().then(async items => {
      if (!analyzeNew || !items) return;
      const fresh = items.filter(
        item =>
          item.user_id === ownerId &&
          item.include_in_processes &&
          !previous.has(item.id) &&
          item.status.status === "not_analyzed",
      );
      await Promise.allSettled(fresh.map(item => analyze_recording(item.id)));
      if (fresh.length) refresh();
    });
  }, [autoAnalyze, ownerId, load, refresh, recordingSyncCompletionId]);
  useEffect(() => {
    for (const item of recordings) {
      if (item.status.status === "analyzing" || item.rerun?.status === "running") {
        // A failed connection needs an explicit retry, not an immediate resubscription loop.
        // An analysis that ended without a result is the recording's own state, which the
        // reloaded list shows on its row.
        subscribe(item.id, refresh, (_, reason) => {
          if (reason === "error") setUpdateError(true);
          else refresh();
        });
      }
    }
  }, [recordings, refresh, subscribe]);
  return {
    recordings,
    update,
    loadedKey,
    loading: loading && loadedKey !== key,
    error,
    updateError,
    revision,
    refresh,
  };
}
